import { useCallback, useEffect, useMemo, useState } from "react";
import { Landmark, Send, FileText, FolderOpen, CreditCard, ShieldAlert } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { canFinView, canFinEdit } from "../lib/roles";
import { http, formatApiError } from "../lib/api";
import { toast } from "../lib/notify";
import FinUploadForm from "../components/fin/FinUploadForm";
import FinFilters from "../components/fin/FinFilters";
import FinDocList from "../components/fin/FinDocList";
import Statements from "../components/fin/Statements";
import BankMailDialog from "../components/fin/BankMailDialog";
import CompanyDocs from "../components/fin/CompanyDocs";
import BankCardsTable from "../components/fin/BankCardsTable";
import { downloadBlob } from "../components/fin/finDownload";
import PdfPreviewPanel from "../components/fin/PdfPreviewPanel";
import { BTN_DARK, docLabel } from "../components/fin/finConst";

const TABS = [["beyanname", "Beyannameler & Mizanlar", FileText], ["evrak", "Şirket Evrakları", FolderOpen], ["banka", "Banka Kartları", CreditCard]];

function BeyannameTab({ companies, canEdit, reloadCompanies }) {
  const [docs, setDocs] = useState([]);
  const [year, setYear] = useState("");
  const [selCo, setSelCo] = useState([]);
  const [selected, setSelected] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [mail, setMail] = useState(false);
  const [preview, setPreview] = useState(null);
  const load = useCallback(() => http.get("/fin/docs").then((r) => setDocs(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const years = useMemo(() => [...new Set(docs.map((d) => d.year))].sort((a, b) => b - a), [docs]);
  const shown = useMemo(() => docs.filter((d) => (!year || String(d.year) === String(year)) && (!selCo.length || selCo.includes(d.company))), [docs, year, selCo]);
  const statementDocs = useMemo(() => (activeId ? shown.filter((d) => d.id === activeId) : selected.length ? shown.filter((d) => selected.includes(d.id)) : []), [shown, activeId, selected]);
  const remove = async (d) => { if (!window.confirm(`${docLabel(d)} — ${d.company} silinsin mi?`)) return; try { await http.delete(`/fin/docs/${d.id}`); toast.success("Silindi"); load(); reloadCompanies(); } catch (err) { toast.error(formatApiError(err)); } };
  return (
    <div className="space-y-5">
      {canEdit && <FinUploadForm companies={companies} onDone={() => { load(); reloadCompanies(); }} />}
      <div className="flex flex-wrap items-center gap-3">
        <FinFilters years={years} year={year} setYear={setYear} companies={companies} selected={selCo} setSelected={setSelCo} />
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500" data-testid="fin-selected-count">{selected.length} belge seçili</span>
          {canEdit && <button type="button" disabled={!selected.length} onClick={() => setMail(true)} className={BTN_DARK} data-testid="fin-send-mail"><Send className="w-3.5 h-3.5" /> Bankaya Mail Gönder</button>}
        </div>
      </div>
      <FinDocList docs={shown} selected={selected} setSelected={setSelected} canEdit={canEdit} onDelete={remove} activeId={activeId} setActiveId={setActiveId}
        onDownload={(d) => downloadBlob(`/fin/docs/${d.id}/file`, d.file_name).catch((err) => toast.error(formatApiError(err)))}
        onView={(d) => setPreview({ path: `/fin/docs/${d.id}/file`, name: d.file_name, title: `${docLabel(d)} · ${d.company}` })} />
      <Statements docs={statementDocs} />
      <PdfPreviewPanel doc={preview} onClose={() => setPreview(null)} />
      {mail && <BankMailDialog title="Bankaya Mail Gönder" summary={`${selected.length} belge ek olarak gönderilecek (PDF'ler PDF, mizanlar Excel olarak)`} onClose={() => setMail(false)}
        onSend={async (sel) => (await http.post("/fin/docs/send-mail", { doc_ids: selected, ...sel })).data} />}
    </div>
  );
}

export default function MaliVeriler() {
  const { user } = useAuth();
  const [tab, setTab] = useState("beyanname");
  const [companies, setCompanies] = useState([]);
  const reloadCompanies = useCallback(() => http.get("/fin/companies").then((r) => setCompanies(r.data)).catch(() => {}), []);
  useEffect(() => { if (canFinView(user)) reloadCompanies(); }, [user, reloadCompanies]);
  if (!canFinView(user)) return <div className="p-8 text-center text-sm text-slate-500 inline-flex items-center gap-2" data-testid="fin-no-access"><ShieldAlert className="w-4 h-4" /> Mali Veriler için yetkiniz yok.</div>;
  const canEdit = canFinEdit(user);
  return (
    <div className="space-y-6" data-testid="mali-veriler">
      <div>
        <h1 className="text-3xl font-bold tracking-tight inline-flex items-center gap-2"><Landmark className="w-7 h-7 text-amber-600" /> Mali Veriler</h1>
        <p className="text-sm text-slate-500 mt-1">Beyannameler (bilanço & gelir tablosu), mizanlar ve şirket evrakları; bankaya tek tıkla gönderim.{!canEdit && " Yalnız görüntüleme yetkiniz var."}</p>
      </div>
      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} type="button" onClick={() => setTab(k)} data-testid={`fin-tab-${k}`} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border-2 text-xs font-bold uppercase transition-colors ${tab === k ? "bg-amber-500 border-amber-500 text-slate-900" : "border-slate-300 text-slate-700 hover:bg-slate-50"}`}><Icon className="w-3.5 h-3.5" /> {label}</button>
        ))}
      </div>
      {tab === "beyanname" && <BeyannameTab companies={companies} canEdit={canEdit} reloadCompanies={reloadCompanies} />}
      {tab === "evrak" && <CompanyDocs companies={companies} canEdit={canEdit} />}
      {tab === "banka" && <BankCardsTable />}
    </div>
  );
}
